import React, { useState, useEffect } from 'react';
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { BaseDialog } from './BaseDialog';
import ProgressTracker from './ProgressTracker';
import { apiFetch } from '../lib/apiFetch';
import { formatCalendarDate, formatBusinessDate, formatBusinessTime } from '../lib/businessTime';

interface OrderDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
}

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    ...shorthands.padding('24px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  label: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    fontWeight: 500,
  },
  value: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    fontWeight: 400,
  },
  statusBadge: {
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderRadius('12px'),
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  historySection: {
    marginTop: '24px',
  },
  historyTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '16px',
  },
  historyTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  historyHeaderRow: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  historyHeaderCell: {
    ...shorthands.padding('12px', '16px'),
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  historyRow: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  historyCell: {
    ...shorthands.padding('12px', '16px'),
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  historyDate: {
    color: tokens.colorNeutralForeground2,
  },
  historyCity: {
    color: tokens.colorNeutralForeground1,
  },
  historyDescription: {
    color: tokens.colorBrandForeground1,
  },
});

// Helper function to map order status to progress step
const getProgressStep = (status: string): number => {
  switch (status) {
    case 'draft':
      return 1; // Ordered
    case 'reserved':
      return 2; // Reserved
    case 'started':
      return 3; // Started
    case 'stopped':
      return 4; // Stopped
    default:
      return 1;
  }
};

// Helper function to get status description
const getStatusDescription = (status: string): string => {
  switch (status) {
    case 'draft':
      return 'Order has been created and is awaiting confirmation';
    case 'reserved':
      return 'Order has been confirmed and items are reserved';
    case 'started':
      return 'Items have been delivered to customer';
    case 'stopped':
      return 'Items have been returned and order is complete';
    default:
      return 'Order status';
  }
};

// Helper function to get status title
const getStatusTitle = (status: string): string => {
  switch (status) {
    case 'draft':
      return 'Ordered';
    case 'reserved':
      return 'Reserved';
    case 'started':
      return 'Started';
    case 'stopped':
      return 'Completed';
    default:
      return 'Order Status';
  }
};

// Helper function to generate activity log entries
const generateActivityLog = (orderData: any) => {
  const attrs = orderData?.attributes || {};
  const activities = [];

  // Created/Ordered activity
  if (attrs.created_at) {
    activities.push({
      date: attrs.created_at,
      city: 'System',
      description: 'Order created',
    });
  }

  // Reserved activity
  if (attrs.confirmed_at || (attrs.status === 'reserved' || attrs.status === 'started' || attrs.status === 'stopped')) {
    activities.push({
      date: attrs.confirmed_at || attrs.created_at,
      city: 'System',
      description: 'Order confirmed and reserved',
    });
  }

  // Started activity
  if (attrs.starts_at && (attrs.status === 'started' || attrs.status === 'stopped')) {
    activities.push({
      date: attrs.starts_at,
      city: attrs.pickup_location || 'N/A',
      description: 'Items delivered to customer',
    });
  }

  // Returned activity (Booqable uses 'stopped' status)
  if (attrs.status === 'stopped' && attrs.stops_at) {
    activities.push({
      date: attrs.stops_at,
      city: attrs.pickup_location || 'N/A',
      description: 'Items returned',
    });
  }

  // Updated activity (if recently updated)
  if (attrs.updated_at && attrs.updated_at !== attrs.created_at) {
    activities.push({
      date: attrs.updated_at,
      city: 'System',
      description: `Order updated - Status: ${attrs.status}`,
    });
  }

  // Sort by date descending (most recent first)
  return activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({ isOpen, onClose, orderId }) => {
  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId || !isOpen) {
        return;
      }

      try {
        setLoading(true);
        const response = await apiFetch(`/booqable/orders/${orderId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch order: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Order data received:', result.data);
        console.log('Order status:', result.data?.attributes?.status);
        setOrderData(result.data);
      } catch (err) {
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, isOpen]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return { backgroundColor: '#F3F2F1', color: '#323130' };
      case 'reserved':
        return { backgroundColor: '#E1F3F8', color: '#004578' };
      case 'started':
        return { backgroundColor: '#E0F5F3', color: '#006B5E' };
      case 'stopped':
        return { backgroundColor: '#E6F0FF', color: '#003F7F' };
      case 'archived':
        return { backgroundColor: '#F3F2F1', color: '#605E5C' };
      case 'canceled':
        return { backgroundColor: '#E8EFF7', color: '#1C3F5C' };
      default:
        return { backgroundColor: '#F3F2F1', color: '#323130' };
    }
  };

  // Start/End dates are rental period values anchored at midnight UTC (no real
  // time component) — render as calendar days in UTC so the day is preserved.
  const formatDate = (dateString: string) => formatCalendarDate(dateString);

  const attrs = orderData?.attributes || {};
  const status = attrs.status === 'started' ? 'started' : attrs.status || 'draft';

  // Progress tracker configuration
  const progressSteps = ['Ordered', 'Reserved', 'Started', 'Completed'];
  const currentStep = getProgressStep(status);
  const statusTitle = getStatusTitle(status);
  const statusDescription = getStatusDescription(status);

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Order Details"
      maxWidth="800px"
      maxHeight="90vh"
      contentPadding="0"
      actions={
        <Button appearance="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
            {loading ? (
              <div className={styles.loadingContainer}>
                <Spinner label="Loading order details..." />
              </div>
            ) : orderData ? (
              <>
                {/* Progress Tracker */}
                <ProgressTracker
                  currentStep={currentStep}
                  steps={progressSteps}
                  title={statusTitle}
                  description={statusDescription}
                />

                <div className={styles.dialogContent}>
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>General Information</div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Order Number</span>
                    <span className={styles.value}>{attrs.number || 'N/A'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Status</span>
                    <span
                      className={styles.statusBadge}
                      style={getStatusColor(status)}
                    >
                      {getStatusTitle(status)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Start Date</span>
                    <span className={styles.value}>
                      {formatDate(attrs.starts_at || attrs.start_at)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>End Date</span>
                    <span className={styles.value}>
                      {formatDate(attrs.stops_at || attrs.stop_at)}
                    </span>
                  </div>
                  {attrs.grand_total_in_cents && (
                    <div className={styles.detailRow}>
                      <span className={styles.label}>Total</span>
                      <span className={styles.value}>
                        ${(attrs.grand_total_in_cents / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                {attrs.note && (
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>Notes</div>
                    <div className={styles.value}>{attrs.note}</div>
                  </div>
                )}

                {/* Activity History */}
                <div className={styles.historySection}>
                  <div className={styles.historyTitle}>History</div>
                  <table className={styles.historyTable}>
                    <thead>
                      <tr className={styles.historyHeaderRow}>
                        <th className={styles.historyHeaderCell}>Date</th>
                        <th className={styles.historyHeaderCell}>City</th>
                        <th className={styles.historyHeaderCell}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generateActivityLog(orderData).map((activity, index) => (
                        <tr key={index} className={styles.historyRow}>
                          <td className={`${styles.historyCell} ${styles.historyDate}`}>
                            {formatBusinessDate(activity.date)}{' '}
                            -{' '}
                            {formatBusinessTime(activity.date)}
                          </td>
                          <td className={`${styles.historyCell} ${styles.historyCity}`}>
                            {activity.city}
                          </td>
                          <td className={`${styles.historyCell} ${styles.historyDescription}`}>
                            {activity.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              </>
            ) : (
              <div className={styles.loadingContainer}>
                <div>No order data available</div>
              </div>
            )}
    </BaseDialog>
  );
};

export default OrderDetailsDialog;
